import ApiError from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiError";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import LazoAuthService from "@/services/LazoAuthService";
import LazoCustomAuthControllerV2Service from "@/services/LazoCustomAuthControllerV2Service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const AUTH_QUERY_KEY = "auth";

/** Matches the backend's in-process cache, so the browser does not out-poll the server's own TTL. */
const FEATURE_FLAGS_STALE_TIME_MS = 5 * 60 * 1000;

// ========== QUERIES ==========

/**
 * Reads the signed-out feature flags. Unauthenticated, so it is safe to call before we know who (or
 * whether) anyone is signed in.
 *
 * Prefer {@link useIsNewLoginEnabled} at call sites: the flag decides whether someone is sent to the
 * new sign-in experience, and a redirect must never fire on a value we do not have yet.
 */
export function useFeatureFlags() {
  return useQuery({
    queryKey: [AUTH_QUERY_KEY, "feature-flags"],
    queryFn: () => LazoAuthService.getFeatureFlags().then((r) => r.getContentOrThrowError()),
    staleTime: FEATURE_FLAGS_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Whether signed-out visitors should go to the standalone auth app.
 *
 * Fails closed while loading and on error, not just when the flag is off: returning `true`
 * optimistically would redirect people mid-fetch, and an unreachable backend would silently opt
 * everyone into a sign-in flow we could not confirm. `isReady` lets a caller wait for a definite
 * answer instead of acting on the interim `false`.
 */
export function useIsNewLoginEnabled(): { enabled: boolean; isReady: boolean } {
  const { data, isSuccess } = useFeatureFlags();

  return { enabled: isSuccess && data?.newLogin === true, isReady: isSuccess };
}

// ========== MUTATIONS ==========

export function useForgotPassword() {
  const { t } = useTranslation("auth");

  return useMutation({
    mutationFn: (email: string) => LazoCustomAuthControllerV2Service.forgotPassword(email).then((r) => r.getContentOrThrowError()),
    mutationKey: [AUTH_QUERY_KEY, "forgot-password"],
    meta: {
      errorMessage: t("forgotPassword.errors.resetError"),
      successMessage: t("forgotPassword.success.emailSent"),
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => LazoAuthService.logout().then((r) => r.getContentOrThrowError()),
    mutationKey: [AUTH_QUERY_KEY, "logout"],
    meta: {
      errorMessage: "Could not log out",
    },
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: ({ email, redirect }: { email: string; redirect?: string }) =>
      LazoAuthService.requestMagicLink(email, redirect).then((r) => r.getContentOrThrowError()),
    mutationKey: [AUTH_QUERY_KEY, "magic-link"],
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        NotificationService.warn(error.message);
      } else {
        NotificationService.error("Something went wrong. Please try again.");
      }
    },
  });
}
